<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import assistantAvatar from '@/assets/customer-service/assistant-dog.png'
import userAvatar from '@/assets/customer-service/user-nezha.jpg'
import { formatDateTime } from '@/utils'

const defaultWelcomeMessage = '粥皮小兵在线为你解答'

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  loadingStarters: {
    type: Boolean,
    default: false,
  },
  sending: {
    type: Boolean,
    default: false,
  },
  messages: {
    type: Array,
    default: () => [],
  },
  welcomeMessage: {
    type: String,
    default: '',
  },
  capabilityLabels: {
    type: Array,
    default: () => [],
  },
  starterQuestions: {
    type: Array,
    default: () => [],
  },
  showStarterState: {
    type: Boolean,
    default: false,
  },
  conversationStatus: {
    type: Number,
    default: 0,
  },
  conversationStatusText: {
    type: String,
    default: '',
  },
  humanAgentName: {
    type: String,
    default: '',
  },
  transferTime: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['feedback', 'quick-question'])
const scrollContainerRef = ref(null)
const scrollAnchorRef = ref(null)

const starterWelcomeText = computed(() => props.welcomeMessage || defaultWelcomeMessage)
const shouldShowStarterState = computed(() => props.showStarterState && !props.messages.length && !props.sending)
const shouldShowBasicEmptyState = computed(
  () => !props.showStarterState && !props.messages.length && !props.sending
)

const statusBanner = computed(() => {
  if (props.conversationStatus === 10) {
    return {
      title: props.conversationStatusText || '等待人工接入',
      desc: props.transferTime
        ? `已提交人工服务，提交时间：${formatDateTime(props.transferTime)}`
        : '已提交人工服务，你可以继续补充问题。',
      type: 'pending',
    }
  }

  if (props.conversationStatus === 20) {
    return {
      title: props.conversationStatusText || '人工客服处理中',
      desc: props.humanAgentName
        ? `${props.humanAgentName} 已接入当前会话，会直接回复你的问题。`
        : '人工客服已接入当前会话，会直接回复你的问题。',
      type: 'manual',
    }
  }

  return null
})

function handleFeedback(messageId, rating) {
  emit('feedback', { messageId, rating })
}

function handleQuickQuestion(question) {
  emit('quick-question', question)
}

function resolveRoleLabel(message) {
  if (message?.role === 'user') {
    return message?.senderName || '我'
  }

  if (message?.sourceType === 'manual') {
    return message?.senderName || '人工客服'
  }

  return message?.senderName || '智能客服'
}

function resolveAvatarLabel(message) {
  if (message?.role === 'user') {
    return '我'
  }

  if (message?.sourceType === 'manual') {
    return '人工'
  }

  return 'AI'
}

function resolveAvatar(message) {
  return message?.role === 'user' ? userAvatar : assistantAvatar
}

function isSameDay(left, right) {
  if (!left || !right) {
    return false
  }

  const leftDate = new Date(left)
  const rightDate = new Date(right)

  return leftDate.getFullYear() === rightDate.getFullYear()
    && leftDate.getMonth() === rightDate.getMonth()
    && leftDate.getDate() === rightDate.getDate()
}

function shouldShowDivider(index) {
  if (index === 0) {
    return true
  }

  return !isSameDay(props.messages[index - 1]?.createdAt, props.messages[index]?.createdAt)
}

function formatDividerLabel(value) {
  const date = new Date(value)
  const today = new Date()

  if (isSameDay(date, today)) {
    return '今天'
  }

  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (isSameDay(date, yesterday)) {
    return '昨天'
  }

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

async function scrollToBottom() {
  await nextTick()
  if (!scrollContainerRef.value) {
    return
  }

  if (scrollAnchorRef.value?.scrollIntoView) {
    scrollAnchorRef.value.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    })
    return
  }

  scrollContainerRef.value.scrollTop = scrollContainerRef.value.scrollHeight
}

watch(
  () => [
    props.messages.length,
    props.sending,
    props.loading,
    props.loadingStarters,
    props.conversationStatus,
    props.welcomeMessage,
    props.messages.map(item => `${item.messageId}-${item.content?.length || 0}`).join('|'),
  ],
  () => {
    scrollToBottom()
  },
  { immediate: true }
)
</script>

<template>
  <div ref="scrollContainerRef" class="customer-service-messages">
    <div v-if="loading" class="customer-service-messages__placeholder">
      正在加载消息...
    </div>

    <template v-else>
      <div v-if="statusBanner" class="customer-service-status" :class="`is-${statusBanner.type}`">
        <div class="customer-service-status__title">{{ statusBanner.title }}</div>
        <div class="customer-service-status__desc">{{ statusBanner.desc }}</div>
      </div>

      <div v-if="shouldShowStarterState" class="customer-service-empty">
        <div class="customer-service-message is-assistant customer-service-empty__welcome-message">
          <div class="customer-service-message__avatar">
            <img
              class="customer-service-message__avatar-image"
              :src="assistantAvatar"
              alt="智能客服"
            >
            <span class="customer-service-message__avatar-badge">AI</span>
          </div>

          <div class="customer-service-message__main">
            <div class="customer-service-message__meta">
              <span class="customer-service-message__sender">智能客服</span>
              <span>刚刚</span>
            </div>

            <div class="customer-service-message__bubble">
              {{ starterWelcomeText }}
            </div>
          </div>
        </div>

        <div v-if="capabilityLabels.length" class="customer-service-empty__tags">
          <span
            v-for="label in capabilityLabels"
            :key="label"
            class="customer-service-empty__tag"
          >
            {{ label }}
          </span>
        </div>

        <div class="customer-service-empty__section">
          <div class="customer-service-empty__section-title">
            {{ loadingStarters ? '正在准备推荐问题...' : '你可以这样问我' }}
          </div>

          <div v-if="starterQuestions.length" class="customer-service-empty__questions">
            <button
              v-for="item in starterQuestions"
              :key="item.question"
              type="button"
              class="customer-service-empty__question"
              @click="handleQuickQuestion(item.question)"
            >
              <span class="customer-service-empty__question-title">{{ item.title }}</span>
              <span class="customer-service-empty__question-text">{{ item.question }}</span>
            </button>
          </div>

          <div v-else class="customer-service-empty__fallback">
            在下方输入框直接提问，例如：这张采购入库单为什么无法提交？
          </div>
        </div>
      </div>

      <div v-else-if="shouldShowBasicEmptyState" class="customer-service-messages__placeholder">
        暂无消息
      </div>

      <div v-else class="customer-service-messages__list">
        <template v-for="(messageItem, index) in messages" :key="messageItem.messageId">
          <div v-if="shouldShowDivider(index)" class="customer-service-messages__divider">
            <span>{{ formatDividerLabel(messageItem.createdAt) }}</span>
          </div>

          <div
            v-if="messageItem.role === 'system'"
            class="customer-service-system"
          >
            <span>{{ messageItem.content }}</span>
          </div>

          <div
            v-else
            class="customer-service-message"
            :class="[
              `is-${messageItem.role}`,
              { 'is-manual-reply': messageItem.sourceType === 'manual' },
            ]"
          >
            <div class="customer-service-message__avatar">
              <img
                class="customer-service-message__avatar-image"
                :src="resolveAvatar(messageItem)"
                :alt="resolveRoleLabel(messageItem)"
              >
              <span class="customer-service-message__avatar-badge">{{ resolveAvatarLabel(messageItem) }}</span>
            </div>

            <div class="customer-service-message__main">
              <div class="customer-service-message__meta">
                <span class="customer-service-message__sender">{{ resolveRoleLabel(messageItem) }}</span>
                <span>{{ formatDateTime(messageItem.createdAt) }}</span>
              </div>

              <div class="customer-service-message__bubble">
                {{ messageItem.content }}
              </div>

              <div
                v-if="messageItem.role === 'assistant' && messageItem.sourceType === 'dify'"
                class="customer-service-message__feedback"
              >
                <el-button
                  text
                  size="small"
                  :type="messageItem.feedbackRating === 'like' ? 'primary' : 'default'"
                  @click="handleFeedback(messageItem.messageId, 'like')"
                >
                  有帮助
                </el-button>
                <el-button
                  text
                  size="small"
                  :type="messageItem.feedbackRating === 'dislike' ? 'danger' : 'default'"
                  @click="handleFeedback(messageItem.messageId, 'dislike')"
                >
                  没帮助
                </el-button>
              </div>
            </div>
          </div>
        </template>

        <div v-if="sending" class="customer-service-message is-assistant is-pending">
          <div class="customer-service-message__avatar">
            <img
              class="customer-service-message__avatar-image"
              :src="assistantAvatar"
              alt="智能客服"
            >
            <span class="customer-service-message__avatar-badge">
              {{ conversationStatus === 20 ? '人工' : 'AI' }}
            </span>
          </div>

          <div class="customer-service-message__main">
            <div class="customer-service-message__meta">
              <span class="customer-service-message__sender">
                {{ conversationStatus === 20 ? (humanAgentName || '人工客服') : '智能客服' }}
              </span>
              <span>正在处理中</span>
            </div>
            <div class="customer-service-message__bubble customer-service-message__bubble--pending">
              <span class="customer-service-message__typing-dot" />
              <span class="customer-service-message__typing-dot" />
              <span class="customer-service-message__typing-dot" />
            </div>
          </div>
        </div>

        <div ref="scrollAnchorRef" class="customer-service-messages__anchor" />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.customer-service-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 16px 12px;
  scroll-behavior: smooth;
}

.customer-service-messages__placeholder {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #7c88a6;
  font-size: 14px;
}

.customer-service-status {
  margin-bottom: 14px;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(75, 116, 213, 0.14);
  background: rgba(255, 255, 255, 0.82);
}

.customer-service-status.is-pending {
  border-color: rgba(212, 140, 49, 0.2);
  background: linear-gradient(135deg, rgba(255, 248, 236, 0.98) 0%, rgba(255, 255, 255, 0.96) 100%);
}

.customer-service-status.is-manual {
  border-color: rgba(31, 161, 128, 0.18);
  background: linear-gradient(135deg, rgba(239, 255, 251, 0.98) 0%, rgba(255, 255, 255, 0.96) 100%);
}

.customer-service-status__title {
  color: #1e3157;
  font-size: 14px;
  font-weight: 700;
}

.customer-service-status__desc {
  margin-top: 6px;
  color: #66779b;
  font-size: 13px;
  line-height: 1.7;
}

.customer-service-empty {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 100%;
  padding: 8px 4px 0;
}

.customer-service-empty__welcome-message {
  margin-top: 4px;
}

.customer-service-empty__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.customer-service-empty__tag {
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(48, 91, 220, 0.08);
  color: #3152a8;
  font-size: 12px;
  font-weight: 600;
}

.customer-service-empty__section {
  padding: 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(99, 132, 214, 0.12);
}

.customer-service-empty__section-title {
  color: #203253;
  font-size: 15px;
  font-weight: 700;
}

.customer-service-empty__questions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.customer-service-empty__question {
  padding: 14px 14px 15px;
  border: 1px solid rgba(88, 122, 210, 0.14);
  border-radius: 18px;
  text-align: left;
  background: linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.customer-service-empty__question:hover {
  transform: translateY(-1px);
  border-color: rgba(60, 102, 215, 0.28);
  box-shadow: 0 14px 28px rgba(51, 83, 162, 0.1);
}

.customer-service-empty__question-title {
  display: block;
  color: #1f3156;
  font-size: 13px;
  font-weight: 700;
}

.customer-service-empty__question-text {
  display: block;
  margin-top: 8px;
  color: #66779b;
  font-size: 13px;
  line-height: 1.7;
}

.customer-service-empty__fallback {
  margin-top: 14px;
  color: #6f7f9f;
  font-size: 13px;
  line-height: 1.7;
}

.customer-service-messages__list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.customer-service-messages__divider {
  display: flex;
  align-items: center;
  justify-content: center;
}

.customer-service-messages__divider span {
  padding: 4px 10px;
  border-radius: 999px;
  color: #6d7c99;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(94, 121, 188, 0.1);
}

.customer-service-system {
  display: flex;
  justify-content: center;
}

.customer-service-system span {
  max-width: min(84%, 680px);
  padding: 10px 14px;
  border-radius: 14px;
  color: #60708f;
  font-size: 12px;
  line-height: 1.7;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(96, 120, 174, 0.12);
}

.customer-service-message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.customer-service-message.is-user {
  flex-direction: row-reverse;
}

.customer-service-message__avatar {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  position: relative;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 10px 24px rgba(48, 74, 127, 0.12);
}

.customer-service-message__avatar-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
}

.customer-service-message__avatar-badge {
  position: absolute;
  right: -1px;
  bottom: -1px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  background: rgba(25, 39, 71, 0.84);
}

.customer-service-message__main {
  max-width: min(82%, 620px);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.customer-service-message__meta {
  display: flex;
  gap: 10px;
  color: #7f8aa3;
  font-size: 12px;
}

.is-user .customer-service-message__meta {
  justify-content: flex-end;
}

.customer-service-message__sender {
  color: #213257;
  font-weight: 700;
}

.customer-service-message__bubble {
  padding: 14px 16px;
  border-radius: 18px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 12px 28px rgba(48, 74, 127, 0.08);
}

.is-user .customer-service-message__bubble {
  border-bottom-right-radius: 8px;
  background: linear-gradient(135deg, #1a8b80 0%, #26b9a8 100%);
  color: #ffffff;
}

.is-assistant .customer-service-message__bubble {
  border-bottom-left-radius: 8px;
  background: #ffffff;
  color: #22304d;
}

.customer-service-message.is-manual-reply .customer-service-message__bubble {
  background: linear-gradient(180deg, #ffffff 0%, #f4fbf8 100%);
  border: 1px solid rgba(30, 161, 129, 0.12);
}

.customer-service-message__bubble--pending {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 50px;
}

.customer-service-message__typing-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6a87d8;
  animation: customer-service-typing 1.2s infinite ease-in-out;
}

.customer-service-message__typing-dot:nth-child(2) {
  animation-delay: 0.15s;
}

.customer-service-message__typing-dot:nth-child(3) {
  animation-delay: 0.3s;
}

.customer-service-message__feedback {
  display: flex;
  gap: 4px;
}

.customer-service-messages__anchor {
  height: 1px;
}

@media (max-width: 768px) {
  .customer-service-empty__questions {
    grid-template-columns: 1fr;
  }

  .customer-service-message__main {
    max-width: 88%;
  }
}

@keyframes customer-service-typing {
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }

  40% {
    opacity: 1;
    transform: translateY(-3px);
  }
}
</style>
